"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { cn } from "@v1/ui/utils";
import { useQuery } from "convex/react";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { ChatLauncherIcon } from "./chat-launcher-icon";
import { type MessengerConfigOn, type MessengerContextValue, MessengerProvider } from "./messenger-context";
import { MessengerPausedPopover } from "./messenger-paused-notice";
import { MessengerDeepLink, MessengerPopover, useLandingOnce, useMemberMessenger } from "./messenger-popover";
import type { MessengerSurface, MessengerView } from "./messenger-types";
import { MessengerWindow } from "./messenger-window";
import { useMessengerNav } from "./use-messenger-nav";

/** Room the bubble takes at the bottom right; other floaters sit above it. */
const LAUNCHER_OFFSET_VAR = "--messenger-launcher-offset";

/** Floating bubble (portal, help center); the app opens from its top bar instead. */
function MessengerBubble({
  open,
  hasUnread,
  iconUrl,
  onToggle,
}: {
  readonly open: boolean;
  readonly hasUnread: boolean;
  /** Org's own icon (Einstellungen › Wissensbereich); null = speech bubble. */
  readonly iconUrl: string | null;
  readonly onToggle: () => void;
}): ReactElement {
  const t = useTranslations("messenger.launcher");
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(LAUNCHER_OFFSET_VAR, "4.5rem");
    return () => {
      root.style.removeProperty(LAUNCHER_OFFSET_VAR);
    };
  }, []);
  return (
    <button
      type="button"
      aria-label={open ? t("schliessen") : t("oeffnen")}
      aria-expanded={open}
      onClick={onToggle}
      className={cn(
        "fixed bottom-5 right-5 z-[1050] flex h-14 w-14 items-center justify-center rounded-full bg-action-500 text-white shadow-[0_6px_24px_rgba(0,0,0,0.18)] transition-transform hover:scale-[1.04] hover:bg-action-600 active:scale-95 print:hidden",
        open && "max-sm:hidden",
      )}
    >
      <ChatLauncherIcon
        iconUrl={iconUrl}
        className={cn("absolute h-6 w-6 transition-all duration-200", open ? "rotate-45 scale-50 opacity-0" : "opacity-100")}
      />
      <ChevronDown
        className={cn("absolute h-6 w-6 transition-all duration-200", open ? "opacity-100" : "-rotate-45 scale-50 opacity-0")}
        strokeWidth={2.2}
        aria-hidden
      />
      {hasUnread && !open ? (
        <span aria-hidden className="absolute right-0.5 top-0.5 h-3 w-3 rounded-full bg-white ring-[3px] ring-action-500" />
      ) : null}
    </button>
  );
}

function MemberBubbleLauncher({ surface }: { readonly surface: MessengerSurface }): ReactElement | null {
  const messenger = useMemberMessenger(surface);
  if (messenger.value === null) return null;
  return (
    <>
      {/* `useSearchParams` needs its own Suspense boundary on static routes. */}
      <Suspense fallback={null}>
        <MessengerDeepLink messenger={messenger} />
      </Suspense>
      {messenger.open ? <MessengerPopover value={messenger.value} placement="bottom" /> : null}
      <MessengerBubble
        open={messenger.open}
        hasUnread={messenger.hasUnread}
        iconUrl={messenger.value.config.launcher_icon_url}
        onToggle={messenger.toggle}
      />
    </>
  );
}

/** Portal (desktop): bubble bottom right, Intercom window above it. */
export function MessengerLauncher({ surface }: { readonly surface: MessengerSurface }): ReactElement | null {
  return <MemberBubbleLauncher surface={surface} />;
}

/** Help-center bubble for anonymous visitors: one chat, start and help. A question from outside opens the chat with it. */
export function MessengerVisitorLauncher({
  orgSlug,
  orgName,
  askRequest = null,
  onAskHandled,
  onAvailability,
}: {
  readonly orgSlug: string;
  readonly orgName: string;
  readonly askRequest?: { readonly id: number; readonly question: string } | null;
  readonly onAskHandled?: () => void;
  readonly onAvailability?: (available: boolean) => void;
}): ReactElement | null {
  const widget = useQuery(api.customerChat.public_widget.getWidgetConfig, { org_slug: orgSlug });
  const nav = useMessengerNav();
  const { reset } = nav;
  const [open, setOpen] = useState<boolean>(false);
  const close = useCallback((): void => setOpen(false), []);
  const available = widget?.state === "ok" && widget.enabled;
  useEffect(() => onAvailability?.(available), [available, onAvailability]);
  useEffect(() => {
    if (askRequest === null || !available) return;
    const question = askRequest.question.trim();
    reset({ kind: "chat", sessionId: null, ...(question ? { pendingText: question } : {}) });
    setOpen(true);
    onAskHandled?.();
  }, [askRequest, available, onAskHandled, reset]);
  const value = useMemo((): MessengerContextValue | null => {
    if (widget?.state !== "ok" || !widget.enabled) return null;
    const config: MessengerConfigOn = {
      state: "on",
      agent_name: widget.agent_name,
      welcome_message: widget.welcome_message ?? "",
      first_name: null,
      help_org_slug: orgSlug,
      launcher_icon_url: widget.launcher_icon_url,
    };
    return { audience: { kind: "visitor", orgSlug, orgName }, config, nav, unread: [], close };
  }, [close, nav, orgName, orgSlug, widget]);
  // Monthly cap hit: the bubble stays, opening shows a pause instead of the chat.
  if (widget?.state === "paused") {
    return (
      <>
        {open ? <MessengerPausedPopover onClose={close} /> : null}
        <MessengerBubble
          open={open}
          hasUnread={false}
          iconUrl={widget.launcher_icon_url}
          onToggle={() => setOpen((current) => !current)}
        />
      </>
    );
  }
  if (value === null) return null;
  return (
    <>
      {open ? <MessengerPopover value={value} placement="bottom" /> : null}
      <MessengerBubble
        open={open}
        hasUnread={false}
        iconUrl={value.config.launcher_icon_url}
        onToggle={() => {
          if (open) {
            setOpen(false);
            return;
          }
          // A visitor has one chat: open it right away, like Intercom.
          reset({ kind: "chat", sessionId: null });
          setOpen(true);
        }}
      />
    </>
  );
}

/** Full-screen messenger (portal phone tab): same window, no bubble; close leaves the tab. */
export function MessengerPage({
  surface,
  onClose,
}: {
  readonly surface: MessengerSurface;
  readonly onClose: () => void;
}): ReactElement | null {
  const config = useQuery(api.customerChat.messenger_lists.getMessengerConfig, { surface });
  const unreadResult = useQuery(
    api.customerChat.messenger_lists.getMyUnread,
    config?.state === "on" ? { surface } : "skip",
  );
  const nav = useMessengerNav();
  const { reset } = nav;
  const land = useCallback((view: MessengerView): void => reset(view), [reset]);
  useLandingOnce(surface, land);
  const unread = unreadResult?.items;
  const value = useMemo(
    (): MessengerContextValue | null =>
      config?.state === "on"
        ? { audience: { kind: "member", surface }, config, nav, unread: unread ?? [], close: onClose }
        : null,
    [config, nav, onClose, surface, unread],
  );
  if (value === null) return null;
  return (
    <MessengerProvider value={value}>
      {/* Covers the portal's own tab bar: one tab bar on screen, never two. */}
      <div className="fixed inset-0 z-[1050] flex flex-col overflow-hidden bg-background pt-[env(safe-area-inset-top,0px)] md:static md:z-auto md:min-h-0 md:flex-1 md:pt-0">
        <MessengerWindow />
      </div>
    </MessengerProvider>
  );
}
